import { graphql, HttpResponse } from "msw";
import { GraphQL } from "@rpi/openapi-api";

// Note: for mock data, only the RECEIVED status is mapped to inProgress to simplify setup.
// In production, data will be mapped to inProgress for all statuses in that category: RECEIVED, QUEUED, VALIDATING, VALID_AWAITING_PAYMENT, VALID_HOLDING_BIN.
const createStatusCounts = (shipped = 0, printing = 0, inProgress = 0, failed = 0) => [
  { status: GraphQL.PublicOrderStatus.Shipped, count: shipped },
  { status: GraphQL.PublicOrderStatus.Printing, count: printing },
  { status: GraphQL.PublicOrderStatus.Received, count: inProgress },
  { status: GraphQL.PublicOrderStatus.Failed, count: failed },
];

/**
 * @featureFlag useMocks
 * @description when active, use any existing mock service worker handlers instead of real calls
 */
// Set Feature Flag if this is a Vercel preview, and it hasn't been set
if (window.location.hostname.includes("vercel.app") && localStorage.getItem("useMocks") === null) {
  localStorage.setItem("useMocks", "true");
}
export const useMocks = localStorage.getItem("useMocks") === "true";
const MOCK_ORDERS_DATA: Record<number, NonNullable<GraphQL.OrdersChartQuery["ordersChart"]>["dataPoints"]> = {
  2024: [
    { month: "2024-01", statusCounts: createStatusCounts(140) },
    { month: "2024-02", statusCounts: createStatusCounts(165, 0, 0, 10) },
    { month: "2024-03", statusCounts: createStatusCounts(175) },
    { month: "2024-04", statusCounts: createStatusCounts(80, 0, 0, 5) },
    { month: "2024-05", statusCounts: createStatusCounts(75) },
    { month: "2024-06", statusCounts: createStatusCounts(80, 0, 0, 5) },
    { month: "2024-07", statusCounts: createStatusCounts(60) },
    { month: "2024-08", statusCounts: createStatusCounts(175) },
    { month: "2024-09", statusCounts: createStatusCounts(175) },
    { month: "2024-10", statusCounts: createStatusCounts(175) },
    { month: "2024-11", statusCounts: createStatusCounts(175) },
    { month: "2024-12", statusCounts: createStatusCounts(300) },
  ],
  2025: [
    { month: "2025-01", statusCounts: createStatusCounts(150) },
    { month: "2025-02", statusCounts: createStatusCounts(180, 0, 0, 8) },
    { month: "2025-03", statusCounts: createStatusCounts(190) },
    { month: "2025-04", statusCounts: createStatusCounts(95, 0, 0, 3) },
    { month: "2025-05", statusCounts: createStatusCounts(85) },
    { month: "2025-06", statusCounts: createStatusCounts(90, 0, 0, 4) },
    { month: "2025-07", statusCounts: createStatusCounts(70, 110, 50, 5) },
    { month: "2025-08", statusCounts: createStatusCounts(175) },
    { month: "2025-09", statusCounts: createStatusCounts(175) },
    { month: "2025-10", statusCounts: createStatusCounts(175) },
    { month: "2025-11", statusCounts: createStatusCounts(175) },
    { month: "2025-12", statusCounts: createStatusCounts(300) },
  ],
  2026: [
    { month: "2026-01", statusCounts: createStatusCounts(170) },
    { month: "2026-02", statusCounts: createStatusCounts(0) },
    { month: "2026-03", statusCounts: createStatusCounts(0) },
    { month: "2026-04", statusCounts: createStatusCounts(0) },
    { month: "2026-05", statusCounts: createStatusCounts(0) },
    { month: "2026-06", statusCounts: createStatusCounts(0) },
    { month: "2026-07", statusCounts: createStatusCounts(0) },
    { month: "2026-08", statusCounts: createStatusCounts(0) },
    { month: "2026-09", statusCounts: createStatusCounts(0) },
    { month: "2026-10", statusCounts: createStatusCounts(0) },
    { month: "2026-11", statusCounts: createStatusCounts(0) },
    { month: "2026-12", statusCounts: createStatusCounts(0) },
  ],
};

const MOCK_PAYMENTS_DATA: Record<number, NonNullable<GraphQL.PaymentsChartQuery["paymentsChart"]>["dataPoints"]> = {
  2024: [
    { month: "2024-01", netPaymentAmount: 1400, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-02", netPaymentAmount: 4650, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-03", netPaymentAmount: 10000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-04", netPaymentAmount: 8000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-05", netPaymentAmount: 7500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-06", netPaymentAmount: 8000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-07", netPaymentAmount: 6000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-08", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-09", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-10", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-11", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-12", netPaymentAmount: 3000, currency: GraphQL.SupportedCurrency.Usd },
  ],
  2025: [
    { month: "2025-01", netPaymentAmount: 1500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-02", netPaymentAmount: 1800, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-03", netPaymentAmount: 19000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-04", netPaymentAmount: 950, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-05", netPaymentAmount: 8500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-06", netPaymentAmount: 9000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-07", netPaymentAmount: 7000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-08", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-09", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-10", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-11", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-12", netPaymentAmount: 3000, currency: GraphQL.SupportedCurrency.Usd },
  ],
  2026: [
    { month: "2026-01", netPaymentAmount: 7000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-02", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-03", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-04", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-05", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-06", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-07", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-08", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-09", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-10", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-11", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-12", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
  ],
};

const AVAILABLE_YEARS = [2024, 2025, 2026];

const MOCK_CUSTOMER_PUBLIC_ID = "CUST-MOCK-001";
type BaseOrder = GraphQL.OrdersQuery["orders"]["data"][number];
type MockOrderOverrides = Partial<Omit<BaseOrder, "customerOrderId">> & {
  customerOrderId: NonNullable<BaseOrder["customerOrderId"]>;
};
const createMockOrder = (overrides: MockOrderOverrides): GraphQL.OrdersQuery["orders"]["data"][number] => ({
  publicOrderStatus: GraphQL.PublicOrderStatus.Shipped,
  createdAt: "2026-03-20T10:00:00Z",
  updatedAt: "2026-03-20T12:00:00Z",
  submittedAt: "2026-03-20T10:00:00Z",
  paymentStatus: GraphQL.PaymentStatus.Paid,
  currency: GraphQL.SupportedCurrency.Usd,
  lastStatusMessage: null,
  isSandbox: false,
  customer: { publicId: MOCK_CUSTOMER_PUBLIC_ID },
  pricing: { currency: GraphQL.SupportedCurrency.Usd, itemsTotal: 50, orderTotal: 60, shippingTotal: 7, taxesTotal: 3 },
  ...overrides,
});

const MOCK_ORDERS_LIST: GraphQL.OrdersQuery["orders"]["data"] = [
  createMockOrder({
    customerOrderId: "ORD-2026-001",
    publicOrderStatus: GraphQL.PublicOrderStatus.Shipped,
    createdAt: "2026-03-15T09:00:00Z",
    updatedAt: "2026-03-22T14:30:00Z",
    submittedAt: "2026-03-15T09:05:00Z",
    pricing: {
      currency: GraphQL.SupportedCurrency.Usd,
      itemsTotal: 2150,
      orderTotal: 2200,
      shippingTotal: 40,
      taxesTotal: 10,
    },
  }),
  createMockOrder({
    customerOrderId: "ORD-2026-002",
    publicOrderStatus: GraphQL.PublicOrderStatus.Printing,
    createdAt: "2026-03-18T14:00:00Z",
    updatedAt: "2026-03-19T08:00:00Z",
    submittedAt: "2026-03-18T14:05:00Z",
    pricing: {
      currency: GraphQL.SupportedCurrency.Usd,
      itemsTotal: 1250,
      orderTotal: 1290,
      shippingTotal: 30,
      taxesTotal: 10,
    },
  }),
  createMockOrder({
    customerOrderId: "ORD-2026-003",
    publicOrderStatus: GraphQL.PublicOrderStatus.Received,
    paymentStatus: GraphQL.PaymentStatus.Unpaid,
    createdAt: "2026-03-25T11:30:00Z",
    updatedAt: "2026-03-25T11:30:00Z",
    submittedAt: null,
    pricing: {
      currency: GraphQL.SupportedCurrency.Usd,
      itemsTotal: 225,
      orderTotal: 250,
      shippingTotal: 20,
      taxesTotal: 5,
    },
  }),
  createMockOrder({
    customerOrderId: "ORD-2026-004",
    publicOrderStatus: GraphQL.PublicOrderStatus.Failed,
    createdAt: "2026-03-10T16:00:00Z",
    updatedAt: "2026-03-11T03:00:00Z",
    submittedAt: "2026-03-10T16:05:00Z",
    lastStatusMessage: "Printer reported an error: STOCK_UNAVAILABLE",
    pricing: {
      currency: GraphQL.SupportedCurrency.Usd,
      itemsTotal: 600,
      orderTotal: 640,
      shippingTotal: 30,
      taxesTotal: 10,
    },
  }),
  createMockOrder({
    customerOrderId: "ORD-2026-005",
    publicOrderStatus: GraphQL.PublicOrderStatus.Shipped,
    currency: GraphQL.SupportedCurrency.Eur,
    createdAt: "2026-03-12T08:00:00Z",
    updatedAt: "2026-03-20T10:00:00Z",
    submittedAt: "2026-03-12T08:10:00Z",
    pricing: {
      currency: GraphQL.SupportedCurrency.Eur,
      itemsTotal: 480,
      orderTotal: 520,
      shippingTotal: 30,
      taxesTotal: 10,
    },
  }),
];

type MockOrderItem = NonNullable<NonNullable<GraphQL.OrderQuery["orderByIdentifier"]>["orderItems"]>[number];

const MOCK_PRODUCT_SPEC = {
  id: 1,
  name: "Standard",
  type: "BOOK",
  version: 1,
  md5Hash: null,
  processingTemplate: "default",
  processingSettings: {},
};

const createMockOrderItem = (overrides: Partial<MockOrderItem> & Pick<MockOrderItem, "id">): MockOrderItem => ({
  openapiProduct: null,
  customerProduct: null,
  printAsset: null,
  orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
  pagecount: 40,
  coverUri: null,
  gutsUri: null,
  imageUri: null,
  validationResult: null,
  pricingAlgorithm: null,
  pricingDetails: null,
  quantity: 1,
  unitPrice: 1.0,
  ...overrides,
});

const MOCK_ITEMS_SHIPPED: MockOrderItem[] = [
  createMockOrderItem({
    id: 1001,
    openapiProduct: {
      id: 10,
      uosSku: "UOS-001",
      openapiSku: "BKPH_SCIW_STD_5.000x8.000IN_80C_GL_40_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Kid's Book",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 20,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "KIDS-BOOK-01",
      name: "Kid's Book",
      description: "For separate packaging",
      openapiProductId: 10,
    },
    printAsset: {
      id: 30,
      customer: null,
      printAssetUploadId: "upload-001",
      status: GraphQL.PrintAssetStatus.Success,
      previews: null,
      preflightOutputFiles: {
        cover: { srcUrl: "https://example.com/files/kids_book_cover.pdf" },
        guts: { srcUrl: "https://example.com/files/kids_book.pdf" },
      },
    },
    orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
    pagecount: 40,
    quantity: 1000,
    unitPrice: 0.9,
  }),
  createMockOrderItem({
    id: 1002,
    openapiProduct: {
      id: 11,
      uosSku: "UOS-002",
      openapiSku: "BKPH_HCIW_STD_7.750x7.750IN_80C_MT_60_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Photo Album",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 21,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "ALBUM-01",
      name: "Photo Album",
      description: null,
      openapiProductId: 11,
    },
    printAsset: {
      id: 31,
      customer: null,
      printAssetUploadId: "upload-002",
      status: GraphQL.PrintAssetStatus.Success,
      previews: null,
      preflightOutputFiles: {
        cover: { srcUrl: "https://example.com/files/album_cover.pdf" },
        guts: { srcUrl: "https://example.com/files/album_pages.pdf" },
      },
    },
    orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
    pagecount: 60,
    quantity: 500,
    unitPrice: 2.5,
  }),
  createMockOrderItem({
    id: 1003,
    openapiProduct: {
      id: 12,
      uosSku: "UOS-003",
      openapiSku: "BKPH_SCIW_STD_8.000x10.000IN_80C_GL_24_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Yearbook",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 22,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "YEAR-01",
      name: "Yearbook",
      description: "Class of 2026",
      openapiProductId: 12,
    },
    printAsset: {
      id: 32,
      customer: null,
      printAssetUploadId: "upload-003",
      status: GraphQL.PrintAssetStatus.Purged,
      previews: null,
      preflightOutputFiles: null,
    },
    orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
    pagecount: 24,
    quantity: 200,
    unitPrice: 3.0,
  }),
  createMockOrderItem({
    id: 1004,
    openapiProduct: {
      id: 13,
      uosSku: "UOS-004",
      openapiSku: "BKPH_SCIW_STD_6.000x9.000IN_80C_GL_80_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Novel",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 23,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "NOVEL-01",
      name: "Novel",
      description: null,
      openapiProductId: 13,
    },
    orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
    pagecount: 80,
    quantity: 50,
    unitPrice: 4.5,
  }),
];

const MOCK_ITEMS_PRINTING: MockOrderItem[] = [
  createMockOrderItem({
    id: 2001,
    openapiProduct: {
      id: 14,
      uosSku: "UOS-005",
      openapiSku: "BKPH_HCIW_STD_8.500x11.000IN_80C_GL_120_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Coffee Table Book",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 24,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "COFFEE-01",
      name: "Coffee Table Book",
      description: "Premium landscape format",
      openapiProductId: 14,
    },
    printAsset: {
      id: 33,
      customer: null,
      printAssetUploadId: "upload-004",
      status: GraphQL.PrintAssetStatus.Success,
      previews: null,
      preflightOutputFiles: {
        cover: { srcUrl: "https://example.com/files/coffee_cover.pdf" },
        guts: { srcUrl: "https://example.com/files/coffee_pages.pdf" },
      },
    },
    orderItemStatus: GraphQL.OrderItemStatus.Processing,
    pagecount: 120,
    quantity: 250,
    unitPrice: 5.0,
  }),
];

const MOCK_ITEMS_RECEIVED: MockOrderItem[] = [
  createMockOrderItem({
    id: 3001,
    openapiProduct: {
      id: 15,
      uosSku: "UOS-006",
      openapiSku: "BKPH_SCIW_STD_5.500x8.500IN_80C_MT_32_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Notebook",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 25,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "NOTE-01",
      name: "Lined Notebook",
      description: null,
      openapiProductId: 15,
    },
    orderItemStatus: GraphQL.OrderItemStatus.AwaitingProcessing,
    pagecount: 32,
    quantity: 100,
    unitPrice: 2.25,
  }),
];

const MOCK_ITEMS_FAILED: MockOrderItem[] = [
  createMockOrderItem({
    id: 4001,
    openapiProduct: {
      id: 16,
      uosSku: "UOS-007",
      openapiSku: "BKPH_SCIW_STD_6.000x9.000IN_80C_GL_200_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Textbook",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 26,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "TEXT-01",
      name: "Intro to Biology",
      description: "University edition, full color",
      openapiProductId: 16,
    },
    printAsset: {
      id: 34,
      customer: null,
      printAssetUploadId: "upload-005",
      status: GraphQL.PrintAssetStatus.Failure,
      previews: null,
      preflightOutputFiles: null,
    },
    orderItemStatus: GraphQL.OrderItemStatus.ProcessingFailed,
    pagecount: 200,
    quantity: 300,
    unitPrice: 2.0,
    validationResult: {
      validations: [
        {
          source: "PrintAssetError-Cover",
          humanReadableString: "An image is of low resolution (Pages: 3, 63, 69)",
          fatalError: true,
        },
        {
          source: "PrintAssetError-Pages",
          humanReadableString: "Pages 21, 23: An image is of low resolution.",
          fatalError: true,
        },
        {
          source: "PrintAssetError-Pages",
          humanReadableString:
            "Pages 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31, 33, 35, 37, 39, 41: Image with non perceptual rendering intent detected.(not yet fatal, might be fixable)",
          fatalError: false,
        },
        {
          source: "PrintAssetAutofix-Cover",
          humanReadableString: "Page 1: Fixing image.",
          fatalError: false,
        },
        {
          source: "PrintAssetAutofix-Pages",
          humanReadableString:
            "Pages 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31, 33, 35, 37, 39, 41: Fixing image.",
          fatalError: false,
        },
      ],
      errors: null,
    },
  }),
  createMockOrderItem({
    id: 4002,
    openapiProduct: {
      id: 17,
      uosSku: "UOS-008",
      openapiSku: "BKPH_HCIW_STD_7.750x7.750IN_80C_GL_48_100T_GLS_44",
      uosMediaOption: null,
      uosOrientation: null,
      name: "Art Portfolio",
      description: null,
      productSpecification: MOCK_PRODUCT_SPEC,
    },
    customerProduct: {
      id: 27,
      customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
      customerSku: "ART-01",
      name: "Art Portfolio",
      description: null,
      openapiProductId: 17,
    },
    printAsset: {
      id: 35,
      customer: null,
      printAssetUploadId: "upload-006",
      status: GraphQL.PrintAssetStatus.Invalid,
      previews: null,
      preflightOutputFiles: null,
    },
    orderItemStatus: GraphQL.OrderItemStatus.ProcessingFailed,
    pagecount: 48,
    quantity: 75,
    unitPrice: 4.0,
    validationResult: {
      validations: [
        {
          source: "PrintAssetError-Pages",
          humanReadableString: "Pages 5, 12: An image is of low resolution.",
          fatalError: true,
        },
        {
          source: "PrintAssetError-CoverAndPages",
          humanReadableString:
            "Pages 1-48: Image with non perceptual rendering intent detected.(not yet fatal, might be fixable)",
          fatalError: false,
        },
        {
          source: "PrintAssetAutofix-CoverAndPages",
          humanReadableString: "Pages 1-48: Fixing image.",
          fatalError: false,
        },
      ],
      errors: null,
    },
  }),
];

const MOCK_ITEMS_BY_ORDER = new Map<string, MockOrderItem[]>([
  ["ORD-2026-001", MOCK_ITEMS_SHIPPED],
  ["ORD-2026-002", MOCK_ITEMS_PRINTING],
  ["ORD-2026-003", MOCK_ITEMS_RECEIVED],
  ["ORD-2026-004", MOCK_ITEMS_FAILED],
  ["ORD-2026-005", MOCK_ITEMS_SHIPPED],
]);

const MOCK_SALE_PAYMENT: GraphQL.OrderQuery["orderByIdentifier"]["salePayment"] = {
  id: 1,
};

const MOCK_REFUNDS: GraphQL.OrderQuery["orderByIdentifier"]["refunds"] = [
  {
    id: 100,
    saleParentPaymentId: 1,
    customerPublicId: MOCK_CUSTOMER_PUBLIC_ID,
    braintreeTransactionId: "txn-refund-001",
    amount: 9.0,
    currency: GraphQL.SupportedCurrency.Usd,
    transactionType: GraphQL.TransactionType.Refund,
    createdAt: "2026-03-28T12:00:00Z",
  },
];

const findMockOrder = (customerOrderId: string): GraphQL.OrderQuery["orderByIdentifier"] | null => {
  const order = MOCK_ORDERS_LIST.find((o) => o.customerOrderId === customerOrderId);
  if (!order) return null;

  const isShipped = order.publicOrderStatus === GraphQL.PublicOrderStatus.Shipped;
  const isPaid = order.paymentStatus === GraphQL.PaymentStatus.Paid;

  return {
    customerOrderId: order.customerOrderId,
    publicOrderStatus: order.publicOrderStatus,
    paymentStatus: order.paymentStatus,
    status: null,
    canReprint: isShipped,
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
    submittedAt: order.submittedAt,
    paymentMode: isPaid ? GraphQL.PaymentMode.Immediate : null,
    currency: order.currency,
    webhookUri: "https://api.example.com/webhooks/order-status",
    lastStatusMessage: order.lastStatusMessage,
    validationResult: null,
    additionalPayload: null,
    canDoFullRefund: isPaid,
    remainingPossibleRefund: (order.pricing?.orderTotal ?? 0) - (customerOrderId === "ORD-2026-001" ? 9.0 : 0),
    isSandbox: order.isSandbox,
    reprintParentCustomerPublicId: customerOrderId === "ORD-2026-002" ? MOCK_CUSTOMER_PUBLIC_ID : null,
    reprintParentCustomerOrderId: customerOrderId === "ORD-2026-002" ? "ORD-2026-001" : null,
    customer: order.customer ? { id: 1, publicId: order.customer.publicId, email: "mock@example.com" } : null,
    pricing: order.pricing,
    destination: MOCK_DESTINATION,
    orderEvents: [],
    callbacks: [
      {
        urlEndpoint: "https://api.example.com/webhooks/order-status",
        attemptCount: 1,
        webhookId: "wh-001",
        webhookStatus: "SUCCESS",
        timeline: null,
        body: null,
      },
    ],
    orderItems: MOCK_ITEMS_BY_ORDER.get(customerOrderId) ?? [],
    shipments: MOCK_SHIPMENTS_BY_ORDER.get(customerOrderId) ?? [],
    salePayment: isPaid ? MOCK_SALE_PAYMENT : null,
    refunds: customerOrderId === "ORD-2026-001" ? MOCK_REFUNDS : [],
  };
};

type MockOrderEvent = NonNullable<NonNullable<GraphQL.OrderEventsQuery["orderByIdentifier"]>["orderEvents"]>[number];

const minutesAgoToTimestamp = (minutesAgo: number): string =>
  new Date(Date.now() - minutesAgo * 60 * 1000).toISOString();

const createMockEvent = (
  id: number,
  eventType: string,
  shortDesc: string,
  minutesAgo: number,
  data?: Record<string, unknown>
): MockOrderEvent => ({
  id,
  eventType,
  shortDesc,
  eventTime: minutesAgoToTimestamp(minutesAgo),
  data: data ?? null,
});

// Realistic happy-path lifecycle using actual OrderEventType values
const MOCK_SHIPPED_EVENTS: MockOrderEvent[] = [
  createMockEvent(12, "ORDER_SHIPPED", "Order has been shipped", 5),
  createMockEvent(11, "ORDER_ACCEPTED_BY_PRINTER", "Order accepted by printer for production", 60),
  createMockEvent(10, "UOS_SUBMISSION", "Order submitted to printer", 65),
  createMockEvent(9, "EXITED_HOLDING_BIN", "Order exited holding bin", 120),
  createMockEvent(8, "ENTERED_HOLDING_BIN", "Order entered holding bin after successful payment", 180),
  createMockEvent(7, "PAYMENT_SUCCESS", "Payment processed successfully", 181),
  createMockEvent(6, "ORDER_FINISHED_PRICING", "Order has been priced", 240),
  createMockEvent(5, "ORDER_ASSETS_PROCESSED", "All assets validated and processed", 300),
  createMockEvent(4, "ORDER_RECEIVED", "Order received for processing", 360),
  createMockEvent(3, "ORDER_CREATED", "Order created via API", 361),
];

// Exception with internal-only events (super admin view)
const MOCK_EXCEPTION_EVENTS: MockOrderEvent[] = [
  createMockEvent(10, "ORDER_EXCEPTION", "Order requires manual intervention", 5),
  createMockEvent(9, "ERROR_DETAILS", "Additional error context from printer", 5, {
    printerError: "STOCK_UNAVAILABLE",
    sku: "BKPH_HCIW_STD_7.750x7.750IN_80C_GL_40_100T_GLS_44",
  }),
  createMockEvent(8, "STAFF_NOTE", "Escalated to production team for resolution", 10),
  createMockEvent(7, "ORDER_ERRORED_BY_PRINTER", "Printer reported an error processing this order", 15),
  createMockEvent(6, "UOS_SUBMISSION", "Order submitted to printer", 60),
  createMockEvent(5, "PAYMENT_SUCCESS", "Payment processed successfully", 120),
  createMockEvent(4, "ORDER_FINISHED_PRICING", "Order has been priced", 180),
  createMockEvent(3, "ORDER_ASSETS_PROCESSED", "All assets validated and processed", 240),
  createMockEvent(2, "ORDER_RECEIVED", "Order received for processing", 300),
  createMockEvent(1, "ORDER_CREATED", "Order created via API", 301),
];

type MockShipment = NonNullable<NonNullable<GraphQL.OrderQuery["orderByIdentifier"]>["shipments"]>[number];
type MockDestination = NonNullable<NonNullable<GraphQL.OrderQuery["orderByIdentifier"]>["destination"]>;

const MOCK_DESTINATION: MockDestination = {
  id: 1,
  shippingMethodCode: "GROUND",
  shippingDetails: { shipMethodName: "Ground Shipping", estimatedDeliveryDateUTC: "2026-04-12T00:00:00Z" },
  address: {
    id: 1,
    createdAt: "2026-03-20T10:00:00Z",
    updatedAt: "2026-03-20T10:00:00Z",
    name: "Jane Smith",
    company: "Acme Publishing",
    address1: "742 Evergreen Terrace",
    address2: null,
    address3: null,
    postalCode: "97475",
    city: "Springfield",
    regionIso: "OR",
    countryIso: "US",
    phone: "+1 (555) 123-4567",
    email: "jane.smith@acme.com",
  },
};

const createMockShipment = (overrides: Partial<MockShipment> & Pick<MockShipment, "id">): MockShipment => ({
  destination: null,
  status: "SHIPPED",
  isFinalShipment: true,
  shippedDate: "2026-03-22T14:00:00Z",
  shippingMethodCode: "GROUND",
  trackingNumber: null,
  masterTrackingNumber: null,
  secondaryTrackingNumber: null,
  rawShipmentData: null,
  orderItems: [],
  ...overrides,
});

const MOCK_SHIPMENTS_SHIPPED: MockShipment[] = [
  createMockShipment({
    id: 501,
    trackingNumber: "1Z999AA10123456784",
    shippingMethodCode: "UPS_GROUND",
    destination: {
      id: 10,
      shippingMethodCode: "UPS_GROUND",
      shippingDetails: {
        shipMethodName: "UPS Ground",
        estimatedDeliveryDateUTC: "2026-04-10T00:00:00Z",
      },
      address: {
        id: 10,
        createdAt: "2026-03-20T10:00:00Z",
        updatedAt: "2026-03-20T10:00:00Z",
        name: "Jane Smith",
        company: "Acme Publishing",
        address1: "742 Evergreen Terrace",
        address2: null,
        address3: null,
        postalCode: "97475",
        city: "Springfield",
        regionIso: "OR",
        countryIso: "US",
        phone: "+1 (555) 123-4567",
        email: "jane.smith@acme.com",
      },
    },
    orderItems: [
      {
        id: 1001,
        quantity: 2,
        pagecount: 40,
        unitPrice: 4.5,
        coverUri: null,
        gutsUri: null,
        imageUri: null,
        orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
        validationResult: null,
        printAsset: {
          id: 2001,
          status: GraphQL.PrintAssetStatus.Success,
          preflightOutputFiles: {
            cover: { srcUrl: "https://example.com/assets/kids_book_cover.pdf" },
            guts: { srcUrl: "https://example.com/assets/kids_book.pdf" },
          },
        },
        customerProduct: {
          id: 1,
          customerSku: "KIDS-BOOK-001",
          name: "My First Book",
          description: "For separate packaging",
        },
        openapiProduct: { id: 10, name: "Kid's Book", openapiSku: "BKPH_SCIW_STD_5.000x8.000IN_80C_GL_40_100T_GLS_44" },
      },
      {
        id: 1002,
        quantity: 1,
        pagecount: 40,
        unitPrice: 9.0,
        coverUri: null,
        gutsUri: null,
        imageUri: null,
        orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
        validationResult: null,
        printAsset: {
          id: 2002,
          status: GraphQL.PrintAssetStatus.Success,
          preflightOutputFiles: {
            cover: { srcUrl: "https://example.com/assets/photo_album_cover.pdf" },
            guts: { srcUrl: "https://example.com/assets/photo_album.pdf" },
          },
        },
        customerProduct: null,
        openapiProduct: {
          id: 11,
          name: "Photo Album",
          openapiSku: "BKPH_HCIW_STD_7.750x7.750IN_80C_GL_40_100T_GLS_44",
        },
      },
    ],
  }),
  createMockShipment({
    id: 502,
    isFinalShipment: false,
    trackingNumber: "9415565SF5A4DNJ",
    shippingMethodCode: "FEDEX_HOME",
    status: "SHIPPED",
    shippedDate: "2026-03-25T09:30:00Z",
    destination: {
      id: 11,
      shippingMethodCode: "FEDEX_HOME",
      shippingDetails: {
        shipMethodName: "FedEx Home Delivery",
        estimatedDeliveryDateUTC: "2026-04-15T00:00:00Z",
      },
      address: {
        id: 11,
        createdAt: "2026-03-20T10:00:00Z",
        updatedAt: "2026-03-20T10:00:00Z",
        name: "John Doe",
        company: null,
        address1: "123 Hillside Ave",
        address2: null,
        address3: null,
        postalCode: "94025",
        city: "Menlo Park",
        regionIso: "CA",
        countryIso: "US",
        phone: "+1 (123) 456-7890",
        email: "johndoe@gmail.com",
      },
    },
    orderItems: [
      {
        id: 1003,
        quantity: 5,
        pagecount: 200,
        unitPrice: 12.0,
        coverUri: null,
        gutsUri: null,
        imageUri: null,
        orderItemStatus: GraphQL.OrderItemStatus.ProcessingSuccess,
        validationResult: null,
        printAsset: {
          id: 2003,
          status: GraphQL.PrintAssetStatus.Success,
          preflightOutputFiles: {
            cover: { srcUrl: "https://example.com/assets/trade_pb_cover.pdf" },
            guts: { srcUrl: "https://example.com/assets/trade_pb.pdf" },
          },
        },
        customerProduct: null,
        openapiProduct: {
          id: 12,
          name: "Trade Paperback",
          openapiSku: "BKPH_SCIW_STD_6.000x9.000IN_80C_MT_200_100T_GLS_44",
        },
      },
    ],
  }),
];

const MOCK_SHIPMENTS_BY_ORDER = new Map<string, MockShipment[]>([
  ["ORD-2026-001", MOCK_SHIPMENTS_SHIPPED],
  ["ORD-2026-005", MOCK_SHIPMENTS_SHIPPED],
]);

const MOCK_EVENTS_BY_ORDER = new Map<string, MockOrderEvent[]>([
  ["ORD-2026-001", MOCK_SHIPPED_EVENTS],
  ["ORD-2026-002", MOCK_SHIPPED_EVENTS.slice(2)],
  ["ORD-2026-003", MOCK_SHIPPED_EVENTS.slice(6)],
  ["ORD-2026-004", MOCK_EXCEPTION_EVENTS],
  ["ORD-2026-005", MOCK_SHIPPED_EVENTS],
]);

export const handlers = [
  graphql.query("Orders", ({ variables }) => {
    const limit = (variables.limit as number) ?? 25;
    const offset = (variables.offset as number) ?? 0;
    const page = MOCK_ORDERS_LIST.slice(offset, offset + limit);

    return HttpResponse.json<{ data: GraphQL.OrdersQuery }>({
      data: {
        orders: {
          totalCount: MOCK_ORDERS_LIST.length,
          offset,
          nextOffset: offset + limit < MOCK_ORDERS_LIST.length ? offset + limit : null,
          prevOffset: offset > 0 ? Math.max(0, offset - limit) : null,
          data: page,
        },
      },
    });
  }),

  graphql.query("Order", ({ variables }) => {
    const { customerPublicId, customerOrderId } = variables.orderIdentifier as {
      customerPublicId: string;
      customerOrderId: string;
    };
    const order = findMockOrder(customerOrderId);

    if (!order || order.customer?.publicId !== customerPublicId) {
      return HttpResponse.json(
        { errors: [{ message: `Mock order not found: ${customerPublicId}/${customerOrderId}` }] },
        { status: 200 }
      );
    }

    return HttpResponse.json<{ data: GraphQL.OrderQuery }>({
      data: { orderByIdentifier: order },
    });
  }),

  graphql.query("OrderItems", ({ variables }) => {
    const { orderIdentifier } = variables as GraphQL.OrderItemsQueryVariables;
    const order = findMockOrder(orderIdentifier.customerOrderId);

    if (!order || order.customer?.publicId !== orderIdentifier.customerPublicId) {
      return HttpResponse.json(
        {
          errors: [
            { message: `Mock order not found: ${orderIdentifier.customerPublicId}/${orderIdentifier.customerOrderId}` },
          ],
        },
        { status: 200 }
      );
    }

    return HttpResponse.json<{ data: GraphQL.OrderItemsQuery }>({
      data: {
        orderByIdentifier: {
          customerOrderId: order.customerOrderId,
          currency: order.currency,
          orderItems: order.orderItems ?? [],
        },
      },
    });
  }),

  graphql.query("OrderDetails", ({ variables }) => {
    const { customerPublicId, customerOrderId } = variables.orderIdentifier as {
      customerPublicId: string;
      customerOrderId: string;
    };
    const order = findMockOrder(customerOrderId);

    if (!order || order.customer?.publicId !== customerPublicId) {
      return HttpResponse.json(
        { errors: [{ message: `Mock order not found: ${customerPublicId}/${customerOrderId}` }] },
        { status: 200 }
      );
    }

    return HttpResponse.json<{ data: GraphQL.OrderDetailsQuery }>({
      data: {
        orderByIdentifier: {
          customerOrderId: order.customerOrderId,
          paymentStatus: order.paymentStatus,
          createdAt: order.createdAt,
          reprintParentCustomerPublicId: order.reprintParentCustomerPublicId,
          reprintParentCustomerOrderId: order.reprintParentCustomerOrderId,
          customer: order.customer
            ? { publicId: order.customer.publicId, organizationName: "Mock Organization" }
            : null,
          pricing: order.pricing,
          salePayment: order.salePayment,
          refunds: order.refunds,
        },
      },
    });
  }),

  graphql.query("OrdersChart", ({ variables }) => {
    const year = variables.year as number;

    return HttpResponse.json<{ data: GraphQL.OrdersChartQuery }>({
      data: {
        ordersChart: {
          availableYears: AVAILABLE_YEARS,
          dataPoints: MOCK_ORDERS_DATA[year] ?? [],
        },
      },
    });
  }),
  graphql.query("OrderEvents", ({ variables }) => {
    const { orderIdentifier } = variables as GraphQL.OrderEventsQueryVariables;
    const customerOrderId = orderIdentifier.customerOrderId;
    const events = MOCK_EVENTS_BY_ORDER.get(customerOrderId) ?? [];

    return HttpResponse.json<{ data: GraphQL.OrderEventsQuery }>({
      data: {
        orderByIdentifier: {
          orderEvents: events,
        },
      },
    });
  }),
  graphql.query("PaymentsChart", ({ variables }) => {
    const year = variables.year as number;

    return HttpResponse.json<{ data: GraphQL.PaymentsChartQuery }>({
      data: {
        paymentsChart: {
          availableYears: AVAILABLE_YEARS,
          nextChargeDate: "2026-02-18T01:00:00Z",
          dataPoints: MOCK_PAYMENTS_DATA[year] ?? [],
        },
      },
    });
  }),

  // Pay Now flow mocks — enables the Pay Now button (paymentMethodMissing: false)
  // and provides mock orders + payment endpoint for local browser testing.
  graphql.query("Bulletins", () => {
    return HttpResponse.json<{ data: GraphQL.BulletinsQuery }>({
      data: {
        bulletins: {
          paymentMethodMissing: false,
          taxDocumentNotUploaded: false,
          ordersLimit: null,
        },
      },
    });
  }),

  graphql.query("ListOrdersForPayment", () => {
    const unpaidOrders = MOCK_ORDERS_LIST.filter(
      (o) => o.paymentStatus === GraphQL.PaymentStatus.Unpaid && o.pricing
    ).map((o) => ({
      customerOrderId: o.customerOrderId,
      createdAt: o.createdAt,
      updatedAt: o.updatedAt,
      pricing: o.pricing!,
    }));

    return HttpResponse.json<{ data: GraphQL.ListOrdersForPaymentQuery }>({
      data: { listOrdersForPayment: unpaidOrders },
    });
  }),

  graphql.mutation("CreateBraintreePayment", () => {
    return HttpResponse.json<{ data: GraphQL.CreateBraintreePaymentMutation }>({
      data: {
        braintreePaymentCreate: {
          message: "Payment successful",
          braintreeTransactionId: "mock-txn-123456",
        },
      },
    });
  }),
];
